import { Box } from '@mui/material';
import ActionsMenu from 'apps/studio/studio-tab/components/actions-menu/ActionsMenu';
import { feedPromptsModalStyles } from 'apps/studio/studio-tab/components/feed-prompts-modal/FeedPromptsModalStyles';

import ShowMoreComponent from 'apps/studio/studio-tab/components/ShowMoreComponent';
import { ReactComponent as Delete } from 'core/assets/svg/export-image/delete.svg';

import { ReactComponent as DownloadDark } from 'core/assets/svg/export-image/download-dark.svg';
import { ReactComponent as DownloadLoading } from 'core/assets/svg/export-image/download-loading.svg';
import { ReactComponent as CopyDetail } from 'core/assets/svg/modal-icons/copy-detail.svg';
import { ReactComponent as Reuse } from 'core/assets/svg/modal-icons/reuse.svg';
import BaseButton from 'core/components/button/BaseButton';
import BaseImage from 'core/components/image/BaseImage';
import BaseTypography from 'core/components/typography/BaseTypography';

import { aspectRatioConfig } from 'core/config/AspectRatioConfig';
import { LayoutOrientation } from 'core/constants/enums/LayoutOrientation.ts';

import { ReusedComponentIDs } from 'core/constants/enums/ReusedComponentIDs.ts';
import { ToastType } from 'core/constants/enums/ToastTypeEnum.ts';
import { sendToast } from 'core/layouts/components/toast/ToastUtils';
import { colorPalette } from 'core/layouts/ui-blocks/ColorPalette';
import { reusableStyles } from 'core/layouts/ui-blocks/ReusableStyles';
import { APP, HISTORY, TEXT_TO_IMAGE } from 'core/navigation/Routes';
import { handleReusePrompt } from 'core/store/reducers/appReducer/appSliceThunk';

import {
	setRandomiseStudioPrompt,
	toggleMultiDeletePromptConfirmationModal,
} from 'core/store/reducers/studioReducer/StudioSlice';

import { copyToClipboard, downloadImage } from 'core/utils/utils';
import React, { useMemo } from 'react';

import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

export default function StyleDetails({
	selectedPrompt,
	handleClose,
	feedPromptsModalType,
	hidePrompt,
	setHidePrompt,
	hideNegativePrompt,
	setHideNegativePrompt,
}) {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const disableButtons = useMemo(() => selectedPrompt.loading, [selectedPrompt.loading]);

	return (
		<Box id="details-cont" sx={feedPromptsModalStyles.promptDetailsCont}>
			<Box
				id="details-scroll-wrapper"
				sx={[
					feedPromptsModalStyles.promptDetailsScrollWrapper,
					reusableStyles.verticalScrollBarStyles,
					{
						'&::-webkit-scrollbar-track': { backgroundColor: 'colors.dark6' },
						'&::-webkit-scrollbar-thumb': { border: `4px solid ${colorPalette.dark6}` },
					},
				]}
			>
				<Box id="prompt-details" sx={feedPromptsModalStyles.detailScreenPromptDetails}>
					<Box id={ReusedComponentIDs.PROMPT_MODAL_SCROLL_IDHAR} />
					<ActionsMenu
						image={selectedPrompt?.media}
						handleClose={handleClose}
						orientation={LayoutOrientation.HORIZONTAL}
						source={feedPromptsModalType}
						imageId={selectedPrompt?.generationId}
					/>
					<Box sx={[feedPromptsModalStyles.detailCard]}>
						<Box id="model-used" sx={[reusableStyles.flexCol]}>
							<Box
								sx={[
									reusableStyles.flexRow,
									{
										width: '100%',
										marginBottom: '6px',
										justifyContent: 'space-between',
									},
								]}
							>
								<BaseTypography variant="b1_medium" styles={{ color: 'colors.light2' }}>
									Style used
								</BaseTypography>

								<Box sx={[reusableStyles.flexRow, { gap: '12px' }]}>
									<Box sx={[reusableStyles.flexRow, { gap: '4px' }]}>
										<Box sx={feedPromptsModalStyles.detailCardModelImage}>
											<BaseImage src={selectedPrompt.styleImage} alt={selectedPrompt.styleLabel} />
										</Box>
										<BaseTypography variant="b2_caption" styles={{ color: 'colors.light2' }}>
											{selectedPrompt.styleLabel}
										</BaseTypography>
									</Box>
								</Box>
							</Box>
						</Box>
					</Box>

					<Box sx={[feedPromptsModalStyles.detailCard]}>
						<Box id="prompt" sx={[reusableStyles.flexCol]}>
							<Box
								sx={[
									reusableStyles.flexRow,
									{
										width: '100%',
										marginBottom: '6px',
										justifyContent: 'space-between',
									},
								]}
							>
								<BaseTypography variant="b1_medium" styles={{ color: 'colors.light2' }}>
									Prompt
								</BaseTypography>

								<Box sx={[reusableStyles.flexRow, { gap: '12px' }]}>
									<Box
										sx={feedPromptsModalStyles.copyDetailButton}
										onClick={(e) => {
											e.stopPropagation();
											copyToClipboard(selectedPrompt.prompt);
											sendToast(ToastType.VALUE_COPIED);
										}}
									>
										<CopyDetail />
									</Box>
								</Box>
							</Box>
							<Box sx={[feedPromptsModalStyles.detailValue]}>
								<BaseTypography variant="b1_regular" styles={{ color: 'colors.light3' }}>
									{selectedPrompt && (
										<ShowMoreComponent
											hide={hidePrompt}
											value={selectedPrompt.prompt ?? ''}
											showMoreFunc={() => setHidePrompt(false)}
										/>
									)}
								</BaseTypography>
							</Box>
						</Box>
					</Box>
					<Box sx={[feedPromptsModalStyles.detailCard]}>
						<Box id="negative-prompt" sx={[reusableStyles.flexCol]}>
							<Box
								sx={[
									reusableStyles.flexRow,
									{
										width: '100%',
										marginBottom: '6px',
										justifyContent: 'space-between',
									},
								]}
							>
								<BaseTypography variant="b1_medium" styles={{ color: 'colors.light2' }}>
									Negative Prompt
								</BaseTypography>

								<Box sx={[reusableStyles.flexRow, { gap: '12px' }]}>
									<Box
										sx={feedPromptsModalStyles.copyDetailButton}
										onClick={(e) => {
											e.stopPropagation();
											copyToClipboard(selectedPrompt.negativePrompt);
											sendToast(ToastType.VALUE_COPIED);
										}}
									>
										<CopyDetail />
									</Box>
								</Box>
							</Box>
							<Box sx={[feedPromptsModalStyles.detailValue]}>
								<BaseTypography variant="b1_regular" styles={{ color: 'colors.light3' }}>
									{selectedPrompt && (
										<ShowMoreComponent
											hide={hideNegativePrompt}
											value={selectedPrompt.negativePrompt ?? ''}
											showMoreFunc={() => setHideNegativePrompt(false)}
										/>
									)}
								</BaseTypography>
							</Box>
						</Box>
						<Box id="prompt-details-divider" sx={feedPromptsModalStyles.divider} />
						<Box id="seed" sx={[reusableStyles.flexCol]}>
							<Box
								sx={[
									reusableStyles.flexRow,
									{
										width: '100%',
										marginBottom: '6px',
										justifyContent: 'space-between',
									},
								]}
							>
								<BaseTypography variant="b1_medium" styles={{ color: 'colors.light2' }}>
									Seed
								</BaseTypography>
								<Box sx={[reusableStyles.flexRow, { gap: '12px' }]}>
									<Box sx={[feedPromptsModalStyles.detailValue]}>
										<BaseTypography variant="b1_regular" styles={{ color: 'colors.light3' }}>
											{selectedPrompt.seed ?? ''}
										</BaseTypography>
									</Box>
									<Box
										sx={feedPromptsModalStyles.copyDetailButton}
										onClick={(e) => {
											e.stopPropagation();
											copyToClipboard(selectedPrompt.seed);
											sendToast(ToastType.VALUE_COPIED);
										}}
									>
										<CopyDetail />
									</Box>
								</Box>
							</Box>
						</Box>
						<Box id="prompt-details-divider" sx={feedPromptsModalStyles.divider} />
						<Box id="aspect-ratio" sx={[reusableStyles.flexCol]}>
							<Box
								sx={[
									reusableStyles.flexRow,
									{
										width: '100%',
										marginBottom: '6px',
										justifyContent: 'space-between',
									},
								]}
							>
								<BaseTypography variant="b1_medium" styles={{ color: 'colors.light2' }}>
									Aspect Ratio
								</BaseTypography>
								<Box sx={[reusableStyles.flexRow, { gap: '12px' }]}>
									<Box sx={[feedPromptsModalStyles.detailValue]}>
										<BaseTypography variant="b1_regular" styles={{ color: 'colors.light3' }}>
											{aspectRatioConfig[selectedPrompt.aspectRatioType]?.label ?? ''}
										</BaseTypography>
									</Box>
								</Box>
							</Box>
						</Box>
					</Box>
				</Box>
			</Box>
			<Box id="move-to-studio-cont-wrapper" sx={feedPromptsModalStyles.detailScreenButtonsCont}>
				<BaseButton
					id="download-prompt"
					styles={[feedPromptsModalStyles.buttonStyles, feedPromptsModalStyles.publishPromptButton]}
					disabled={disableButtons}
					onClick={(e) => {
						e.stopPropagation();
						downloadImage(selectedPrompt?.image || '');
					}}
				>
					{disableButtons ? <DownloadLoading /> : <DownloadDark />}
					<BaseTypography variant="b1_bold" styles={{ color: 'inherit' }}>
						Download
					</BaseTypography>
				</BaseButton>
				<Box sx={[reusableStyles.flexRow, { flexGrow: 1, gap: 'inherit' }]}>
					<BaseButton
						id="delete-button"
						disabled={disableButtons}
						styles={[feedPromptsModalStyles.buttonStyles, feedPromptsModalStyles.downloadButton]}
						onClick={() => {
							dispatch(
								toggleMultiDeletePromptConfirmationModal({
									open: true,
									prompt: [
										{
											generationId: selectedPrompt?.generationId,
											batchId: selectedPrompt?.batchId,
										},
									],
								}),
							);
						}}
					>
						<Delete />
					</BaseButton>
					<BaseButton
						id="reuse-prompt"
						styles={[
							feedPromptsModalStyles.buttonStyles,
							feedPromptsModalStyles.downloadButton,
							{
								backgroundColor: selectedPrompt.published ? 'colors.light1' : 'colors.dark3',
								'&:hover': {
									backgroundColor: selectedPrompt.published ? 'colors.light1' : 'colors.dark3',
								},
							},
						]}
						onClick={(e) => {
							e.stopPropagation();
							dispatch(handleReusePrompt());
							if (feedPromptsModalType === HISTORY) {
								navigate(`/${APP}/${TEXT_TO_IMAGE}`);
								// NOTE: SETTING RANDOMISE STUDIO PROMPT TO FALSE
								dispatch(setRandomiseStudioPrompt(false));
							}
							handleClose();
						}}
					>
						<Reuse />
					</BaseButton>
				</Box>
			</Box>
		</Box>
	);
}
